import { ChangeDetectionStrategy, Component, inject, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AlertService } from '@app/core';
import { PaisEnum } from '@app/core/models';
import { ShowcaseEntity, ShowcaseType } from '@app/core/models/showcase.model';
import { LearningTrailsShowcaseService } from '@app/core/services/learning-trails-showcase.service';
import { notNull, safeEmpty, safeEmptyList, selectedEntityActive } from '@app/shared/utils';
import { SIDENAV_DATA, SIDENAV_REF, SidenavRef } from '@app/shared/modules/template/components/sidenav';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, combineLatest, filter, map, switchMap, take, tap } from 'rxjs';

@Component({
  templateUrl: './sidenav-learning-studio-showcase.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidenavLearningStudioShowcaseComponent {
  private readonly _store = inject<Store<AppState>>(Store);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _alertService = inject(AlertService);
  private readonly _translateService = inject(TranslateService);
  private readonly _learningTrailsShowcaseService = inject(LearningTrailsShowcaseService);
  public readonly course = inject(SIDENAV_DATA);
  private readonly sidenavRef = inject<SidenavRef>(SIDENAV_REF);

  public form: FormGroup;
  public isShowcase = true;
  public searchCtrl = new FormControl(undefined);
  public showInput: boolean = false;
  public paisId$: Observable<number>;

  public showcases$ = new BehaviorSubject<any[]>([]);
  public loading$ = new BehaviorSubject<boolean>(false);
  public showcaseTypes$: Observable<ShowcaseType[]>;
  public entitieTypeFilter$ = new BehaviorSubject<any>(undefined);
  public entities$: Observable<ShowcaseEntity[]>;

  constructor() {
    this.loadShowcases();
    this.loadShowcaseTypes();
    this.loadEntities();
    this.initForm();
    this.paisId$ = this._store.select(AppSelectors.ActiveUser).pipe(map((user) => user?.pais_id));
  }

  public onClose() {
    this.sidenavRef.close();
  }

  public onToggleInput() {
    this.showInput = !this.showInput;
  }

  public onToggleShowcase(showcase: any = null) {
    this.isShowcase = !this.isShowcase;
    if (this.isShowcase) this.resetForm();
    if (showcase) this.fillForm(showcase);
  }

  public onSaveShowcase() {
    if (this.form.controls.titulo.invalid || this.form.controls.tipo.invalid) {
      this.form.markAllAsTouched();
      return;
    } else if (this.form.controls.entidades.invalid) {
      this._alertService.alert({ message: 'trilhas.estudio.vitrine.entidade' });
      return;
    }

    this._alertService
      .confirm({ message: this._translateService.instant('trilhas.estudio.vitrine.deseja-salvar') })
      .pipe(take(1), notNull())
      .subscribe(() => this.saveShowcase());
  }

  public onDeteleShowcase(showcaseId: number) {
    this._alertService
      .confirm({ message: this._translateService.instant('trilhas.estudio.vitrine.deseja-excluir') })
      .pipe(take(1), notNull())
      .subscribe(() => this.deleteShowcase(showcaseId));
  }

  public onToggleEntityCheckbox(event: any, entity: ShowcaseEntity) {
    const entidades = this.form.controls.entidades.value || [];
    this.form.controls.entidades.setValue(
      event.target.checked ? [...entidades, entity.id] : entidades.filter((id) => id !== entity.id),
    );
  }

  public onMarkAllEntities(entities: ShowcaseEntity[]) {
    this.form.controls.entidades.setValue(entities.map((entity) => entity.id));
  }

  public onUnmarkAllEntities() {
    this.form.controls.entidades.setValue([]);
  }

  public isCheckboxChecked(entityId: number): boolean {
    return this.form.controls.entidades.value.includes(entityId);
  }

  public isAllChecked(entities: ShowcaseEntity[]): boolean {
    const entidades = this.form.controls.entidades.value || [];
    return entidades.length === entities.length && entities.length > 0;
  }

  public hasAnyChecked(): boolean {
    const entidades = this.form.controls.entidades.value || [];
    return entidades.length > 0;
  }

  private loadShowcases() {
    this.loading$.next(true);
    this._store
      .select(AppSelectors.ActiveUser)
      .pipe(
        switchMap((activeUser) =>
          this._learningTrailsShowcaseService.listShowcase(this.course.id).pipe(
            safeEmptyList(),
            map((showcases) =>
              showcases.map((showcase: any) =>
                activeUser.pais_id === PaisEnum.Brasil
                  ? showcase
                  : {
                      ...showcase,
                      tipo: {
                        ...showcase.tipo,
                        descricao: showcase.tipo.descricao_es,
                      },
                    },
              ),
            ),
          ),
        ),
        tap(() => this.loading$.next(false)),
      )
      .subscribe(this.showcases$);
  }

  private loadShowcaseTypes() {
    const types$ = this._learningTrailsShowcaseService.getShowcaseTypes().pipe(safeEmptyList());
    const userEntity$ = this._store.select(AppSelectors.ActiveUser).pipe(
      notNull(),
      map(selectedEntityActive),
      filter((entities) => !!entities && entities.length > 0),
      map((entities) => entities[0]),
    );

    this.showcaseTypes$ = combineLatest([types$, userEntity$]).pipe(
      map(([types, entity]) => types.filter((type) => type.tipo <= entity.tipo).sort((a, b) => b.tipo - a.tipo)),
    );
  }

  private loadEntities() {
    const entities = this._store.select(AppSelectors.ActiveUser).pipe(
      map((user) => user?.paises ?? user?.pais_id),
      switchMap((pais_id) => this._learningTrailsShowcaseService.getEntities(pais_id).pipe(safeEmptyList())),
    );

    this.entities$ = combineLatest([entities, this.entitieTypeFilter$]).pipe(
      filter(([entities, filter]) => !!filter),
      map(([entities, filter]) =>
        entities.filter((entity) => entity.tipo === filter.type && entity.externo === filter.external),
      ),
    );
  }

  private initForm() {
    this.form = this._formBuilder.group({
      id: undefined,
      curso_id: this.course.id,
      titulo: [undefined, Validators.required],
      tipo: [undefined, Validators.required],
      tipo_id: undefined,
      professor: [false, Validators.required],
      gerar_evento: [false, Validators.required],
      relatorio: [false, Validators.required],
      entidades: [[], Validators.required],
    });

    this.form.controls.tipo.valueChanges.subscribe((type: ShowcaseType) => {
      this.form.controls.tipo_id.setValue(type.id);
      this.entitieTypeFilter$.next({ type: type.tipo, external: type.externo });

      this._store
        .select(AppSelectors.ActiveUser)
        .pipe(
          notNull(),
          map(selectedEntityActive),
          filter((entities) => !!entities && entities.length > 0),
          map((entities) => entities[0]),
        )
        .subscribe((entity) => {
          if (entity.tipo === type.tipo) {
            this.form.controls.entidades.setValue([entity.id]);
          } else {
            this.form.controls.entidades.setValue([]);
          }
        });
    });
  }

  private resetForm() {
    this.form.patchValue(
      {
        id: undefined,
        curso_id: this.course.id,
        titulo: undefined,
        tipo: undefined,
        tipo_id: undefined,
        professor: false,
        gerar_evento: false,
        relatorio: false,
        entidades: [],
      },
      { emitEvent: false },
    );
    this.form.markAsUntouched();
    this.entitieTypeFilter$.next(undefined);
  }

  private fillForm(showcase: any) {
    const body = {
      ...showcase,
      professor: !!showcase.professor,
      gerar_evento: !!showcase.gerar_evento,
      relatorio: !!showcase.relatorio,
    };
    this.form.patchValue(body);
  }

  private saveShowcase() {
    const body = this.form.value;
    const request = body.id
      ? this._learningTrailsShowcaseService.updateShowcase(body.id, body)
      : this._learningTrailsShowcaseService.postShowcase(body);

    request.pipe(safeEmpty()).subscribe((data) => {
      this.showcases$.next([...this.showcases$.value.filter((showcase: any) => showcase.id !== data.id), data]);
      this.onToggleShowcase();
    });
  }

  private deleteShowcase(showcaseId: number) {
    this._learningTrailsShowcaseService.deleteShowcase(showcaseId).subscribe(() => {
      this.showcases$.next(this.showcases$.value.filter((showcase: any) => showcase.id !== showcaseId));
    });
  }
}
